"use client";

import confetti from "canvas-confetti";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Home, Key } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CelebrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  buyerName?: string;
}

export function CelebrationModal({
  isOpen,
  onClose,
  buyerName,
}: CelebrationModalProps) {
  useEffect(() => {
    if (isOpen) {
      // Trigger confetti burst
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 2,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#FF6B6B", "#4ECDC4", "#45B7D1"],
        });
        confetti({
          particleCount: 2,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#FF6B6B", "#4ECDC4", "#45B7D1"],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center space-y-6 py-4"
            >
              <DialogHeader className="text-center">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mx-auto"
                >
                  <div className="relative">
                    <div className="absolute -top-1 left-1/2 -ml-3">
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: "reverse",
                        }}
                      >
                        <Heart className="h-6 w-6 text-red-500" />
                      </motion.div>
                    </div>
                    <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <Home className="h-10 w-10 text-primary" />
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <DialogTitle className="text-2xl font-bold mt-4 text-center">
                    Welcome Home{buyerName ? `, ${buyerName}` : ""}! 🎉
                  </DialogTitle>
                </motion.div>
              </DialogHeader>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="space-y-6 px-4"
              >
                <p className="text-muted-foreground">
                  Congratulations on this incredible milestone! Your new chapter
                  of homeownership begins now. We're honored to have been part
                  of your journey.
                </p>

                <div className="grid grid-cols-3 gap-4 my-6">
                  {[
                    {
                      icon: Key,
                      label: "Keys in Hand",
                    },
                    {
                      icon: Home,
                      label: "Home Sweet Home",
                    },
                    {
                      icon: Heart,
                      label: "New Memories",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <item.icon className="h-6 w-6 text-primary" />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {item.label}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border p-4 bg-primary/5">
                    <h3 className="font-semibold mb-2">
                      Maintenance Mode - $5/mo
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Keep access to all your documents and home information
                      while maintaining a simplified dashboard for ongoing
                      homeownership.
                    </p>
                    <Button className="w-full" variant="default" size="lg">
                      Switch to Maintenance Mode
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Button className="w-full" variant="ghost" size="sm">
                      Cancel Subscription
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      You'll still have 30 days to download your documents
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
